import React, { useState } from "react";
import { useSelector } from "react-redux";

/**
 * ExportButtons - Botón de exportación Excel con descarga autenticada.
 *
 * Props:
 * - exportUrl: string - URL del endpoint de exportación
 * - onExportExcel: () => Promise<void>|void (optional) - Override callback
 * - label: string (optional) - Texto del botón (default: "Exportar Excel")
 * - disabled: boolean (optional) - Deshabilita el botón
 */
export function ExportButtons({
  exportUrl,
  onExportExcel,
  label = "Exportar Excel",
  disabled = false,
}) {
  const [exporting, setExporting] = useState(false);
  const token = useSelector((state) => state.app.user?.token);

  const handleExport = async () => {
    if (disabled || exporting) return;

    setExporting(true);
    try {
      if (onExportExcel) {
        await onExportExcel();
      } else if (exportUrl) {
        // Fetch with auth header and download as blob
        const response = await fetch(exportUrl, {
          method: "GET",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // Extract filename from Content-Disposition header or use default
        const disposition = response.headers.get("Content-Disposition");
        const filename = disposition
          ? disposition.split("filename=")[1]?.replace(/"/g, "")
          : "export.xlsx";

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Error al exportar:", err);
      alert("Error al exportar. Intente nuevamente.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={disabled || exporting}
      className="inline-flex items-center gap-2 bg-green text-white text-sm font-medium py-2 px-4 rounded hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      {exporting ? "Exportando..." : label}
    </button>
  );
}

export default ExportButtons;
